import { Col, Form, Row } from "antd"
import { useMemo } from "react"
import { Button, Checkbox, DataTable, Icon, InputNumber, SelectInput, TextInput, TreeSelectInput } from "../../common"
import styles from "./index.module.less"
import { t } from "i18next"

const dependentsObj = dependents => {
  let obj = {}
  dependents?.forEach(item => {
    obj = { ...obj, [item]: undefined }
  })
  return obj
}

const FieldsTable = props => {
  const {
    fieldsList = () => {},
    onAdd = () => {},
    onRemoveItem = () => {},
    valuesList = [],
    setCurrentSelectedValues = () => {},
    transformTextInput = null,
    headerSticky = true,
    tableStickyOffset,
    disableActions = false,
    fieldFlex = "20%",
    wrap = false,
    fieldsRowClass,
    editable = false,
    editingIndex,
    customSelectRender,
    getEditIcons = () => {},
    disableAdd = false,
    placeholder = "-",
    addMarginTop = 0,
    serialNumber,
    addButtonProps = { addButtonIcon: "HiPlus", addButtonText: t("Add") },
    extraColumns = [],
    extraColumnValues = () => {},
    tableHeader = null,
    fixedActionColumn = undefined,
    dataTableProps,
    disbaleIcon,
    /** When true, Add sits immediately after the last field (same flex row). Default false keeps legacy 21/3 grid. */
    inlineAddButton = false,
    fieldSpace,
  } = props
  const [form] = Form.useForm()
  const [editform] = Form.useForm()
  const columns = useMemo(() => {
    if (!valuesList?.length) return []
    let tableColumns = []
    tableColumns.push({ title: serialNumber, dataIndex: "serial", hide: !serialNumber, width: 50 })
    fieldsList(form).forEach(item => {
      tableColumns.push({
        title: item.label,
        dataIndex: item.name,
        type: item.type,
        hide: item?.hide,
        width: item?.width,
        className: item?.className,
        fixed: item?.fixed,
      })
    })
    tableColumns.push(...extraColumns)
    tableColumns.push({ title: t("Action"), dataIndex: "actions", hide: disableActions, fixed: fixedActionColumn })
    tableColumns = tableColumns.filter(item => !item?.hide) // allows you to remove the columns you dont want to show

    return tableColumns
  }, [valuesList])

  const onSubmit = async values => {
    onAdd(values)
    form.resetFields()
  }

  const getFieldItem = item => {
    const {
      type,
      label,
      placeholder,
      name,
      options,
      rules,
      defaultValue,
      validations,
      componentProps,
      formProps,
      dependencies,
      dependents,
      onChange = () => {},
      extra,
      addonAfter,
      ...rest
    } = item
    switch (type) {
      case "select":
        return (
          <Form.Item dependencies={dependencies} name={name} rules={rules} {...formProps}>
            <SelectInput
              className="selectSearch"
              options={options}
              placeholder={label}
              {...componentProps}
              {...rest}
              onChange={(value, option) => {
                componentProps?.mode === "multiple"
                  ? handleMultiValuesSelect(option, name)
                  : form.setFieldsValue({
                      [name]: { ...option, label: option?.label, value: option?.value },
                      ...dependentsObj(dependents),
                    })

                componentProps?.onChange && componentProps.onChange(value, form)
              }}
              getPopupContainer={false}
              openIcon="MdKeyboardArrowUp"
            />
          </Form.Item>
        )

      case "input":
        return (
          <Form.Item dependencies={dependencies} name={name} rules={rules} {...formProps}>
            <TextInput placeholder={label} suffixIcon="search" {...componentProps} />
          </Form.Item>
        )

      case "checkbox":
        return (
          <Form.Item dependencies={dependencies} name={name} rules={rules} {...formProps}>
            <Checkbox
              onChange={value => {
                form.setFieldsValue({ [name]: value.target.checked })
              }}
              {...componentProps}
            >
              {label}
            </Checkbox>
          </Form.Item>
        )
      case "treeSelect":
        return (
          <Form.Item dependencies={dependencies} name={name} rules={rules} {...formProps}>
            <TreeSelectInput
              className="selectSearch"
              options={options}
              placeholder={label}
              onChange={(value, label, extra) => {
                form.setFieldsValue({ [name]: value })
                componentProps?.onChange && componentProps.onChange((value, label, extra))
              }}
              {...componentProps}
            />
          </Form.Item>
        )
      case "number":
        return (
          <>
            <Form.Item dependencies={dependencies} name={name} rules={rules} {...formProps}>
              <InputNumber
                style={{ width: "100%" }}
                placeholder={label}
                onChange={amount => onChange?.(amount)}
                addonAfter={addonAfter}
                {...componentProps}
              />
            </Form.Item>
            {extra}
          </>
        )

      default:
        return <></>
    }
  }
  const getSelectValue = obj =>
    customSelectRender ? customSelectRender(obj) : obj?.label || obj?.name || (typeof obj === "object" ? "-" : obj)

  const getTableValue = (fieldItem, obj, index) => {
    const customItemRender = fieldItem?.componentProps?.customRender
    switch (fieldItem.type) {
      case "select":
        return index === editingIndex && fieldItem.editable ? (
          <Form form={editform}>{getFieldItem({ ...fieldItem, name: fieldItem?.name + index })}</Form>
        ) : Array.isArray(obj[fieldItem.name]) ? (
          obj[fieldItem.name].map(e => getSelectValue(e))
        ) : (
          getSelectValue(obj[fieldItem.name])
        )
      case "treeSelect":
        return Array.isArray(obj[fieldItem.name])
          ? obj[fieldItem.name].map(e => (customItemRender ? customItemRender(e) : getSelectValue(e)))
          : customItemRender
          ? customItemRender(obj[fieldItem.name])
          : getSelectValue(obj[fieldItem.name])
      case "checkbox":
        return obj[fieldItem.name] ? "Yes" : "No"
      case "number":
        return obj[fieldItem.name]
      default:
        return index === editingIndex && fieldItem.editable ? (
          <Form form={editform}>{getFieldItem({ ...fieldItem, name: index })}</Form>
        ) : transformTextInput ? (
          transformTextInput(obj[fieldItem.name], index)
        ) : (
          obj[fieldItem.name]
        )
    }
  }

  const tableData = useMemo(() => {
    if (!valuesList?.length) return []
    const data = valuesList.map((obj, index) => {
      const row = { key: index }
      fieldsList(form).forEach(it => {
        const value = getTableValue(it, obj, index)
        row[it.name] = value ? value : value === 0 ? placeholder : "-"
      })
      const extraValues = extraColumnValues(obj) ?? {}
      Object.keys(extraValues)?.map(key => (row[key] = extraValues?.[key]))
      row.actions = !editable ? (
        <Icon color="#E83A3A" icon="FiTrash2" size="18px" onClick={() => onRemoveItem(index)} disabled={disbaleIcon}/>
      ) : (
        getEditIcons(index, editform)
      )
      serialNumber && (row["serial"] = index + 1)
      return row
    })
    return data
  }, [valuesList, editable, editingIndex, extraColumnValues])

  const handleMultiValuesSelect = (option, key) => {
    form.setFieldsValue({ [key]: option })
  }

  const renderFieldItem = (item, index) => {
    return !item?.fieldHide ? ( // allows you to hide fields based on field props
      <Col flex={fieldFlex} key={index}>
        {getFieldItem(item)}
      </Col>
    ) : (
      <></>
    )
  }

  const addRowClassName = [fieldsRowClass, "w-100"].filter(Boolean).join(" ")

  const renderAddButton = ({ block: blockProp }) => (
    <Button
      icon={addButtonProps?.addButtonIcon}
      type="primary-light"
      text={addButtonProps?.addButtonText}
      onClick={form.submit}
      block={blockProp}
      size="large"
      style={{ marginTop: addMarginTop, height: 48 }}
    />
  )

  return (
    <>
      {!disableAdd && (
        <Form
          form={form}
          layout="inline"
          onFinish={onSubmit}
          onValuesChange={() => setCurrentSelectedValues(form.getFieldsValue())}
          scrollToFirstError
          preserve={false}
          className="mbe-16"
          // style={{ maxWidth: "none" }}
          // wrapperCol={{ span: 24 }}
        >
          {inlineAddButton ? (
            <Row wrap={wrap} gutter={[8, 0]} align="middle" className={addRowClassName}>
              {fieldsList(form)?.map((item, index) => renderFieldItem(item, index))}
              <Col flex="none">{renderAddButton({ block: false })}</Col>
            </Row>
          ) : (
            <Row gutter={0} className="w-100">
              <Col span={ fieldSpace || 21}>
                <Row wrap={wrap} className={fieldsRowClass}>
                  {fieldsList(form)?.map((item, index) => renderFieldItem(item, index))}
                </Row>
              </Col>
              <Col span={3}>{renderAddButton({ block: true })}</Col>
            </Row>
          )}
        </Form>
      )}
      {valuesList?.length > 0 && (
        <>
          {tableHeader}
          <DataTable
            className={styles.uniTargetTable}
            data={tableData}
            columns={columns}
            headerSticky={headerSticky}
            stickyOffset={tableStickyOffset}
            {...dataTableProps}
          />
        </>
      )}
    </>
  )
}
export default FieldsTable

/**
 * Usage *
 * 
 * 
 *   
  const [values, setValues] = useState([])
  const fieldList = useMemo(() => [
    {
      label: "Feature",
      placeholder: "Select Feature",
      name: "feature",
      type: "select",
      options: [
        { label: "Corner", value: 1 },
        { label: "Park Facing", value: 2 },
        { label: "Roadside", value: 3 },
      ],
      rules: [{ message: "Please select feature", required: true }],
      componentProps: { allowClear: true },
    },
    {
      label: "Amount",
      placeholder: "Enter Amount",
      name: "amount",
      type: "checkbox",
    },
    {
      label: "Type",
      placeholder: "Select Type",
      name: "type",
      type: "select",
      options: [
        { label: "Amount", value: "amount" },
        { label: "Percentage", value: "percentage" },
      ],
      rules: [{ message: "Please select amount type", required: true }],
    },
    {
      label: "Check",
      name: "check",
      type: "input",
      rules: [{ message: "Please add amount", required: true }],
      componentProps: { type: "number" },
    },
  ])

  return (
    <FieldsTable
      fieldsList={fieldList}
      onAdd={item => setValues(prev => [...prev, item])}
      onRemoveItem={index => setValues(prev => prev.filter((item, i) => i !== index))}
      valuesList={values}
    />
  )
 * 
 * 
 * 
 */
